import { useEffect, useRef, useState } from 'react';

import { Notice } from '@/components/Modal';
import { today } from '@/pages/sites/pmis/components/format';
import {
  CHART,
  currentGap,
  linePath,
  monthTicks,
  plotOf,
  todayIndex,
  xAt,
  yAt,
} from '@/pages/sites/pmis/components/progressCurve';
import type { PmisProgressCurve } from '@/pages/sites/pmis/queries';

/**
 * 공정율 그래프 — 예정(S-curve)과 실적을 겹쳐 그린다 (DOC-33).
 *
 * <h2>왜 표만으로는 부족한가</h2>
 * 표는 "지금 31.2%" 를 말하지만 <b>앞서는지 밀리는지</b>는 말하지 않는다. 발주처 보고에서
 * 먼저 묻는 것이 그것이다. 두 곡선을 겹치면 그 답이 한눈에 보인다.
 *
 * <h2>숫자는 서버 것을 그대로 쓴다</h2>
 * 곡선의 값은 {@code WbsService#progressCurve} 가 접어 준 것이다. 화면은 좌표로 옮기기만 한다 —
 * 표의 롤업 숫자와 그래프가 갈라지면 어느 쪽이 정본인지 아무도 모른다.
 */

const PLANNED_COLOR = '#94a3b8';

/**
 * 실적선은 <b>주간회의 보고 공정율 하나</b>다(사용자 지시 2026-10-01).
 *
 * <p>2026-08-14 에는 작업일보 롤업 선을 따로 겹쳐 그렸는데, 일보에 공정별 실적을 꼬박 적지 않는
 * 현장에서는 그 선이 늘 아래에 깔려 혼란만 주었다. 서버가 주는 {@code actualRate} 는 그대로 두고
 * 화면만 안 그린다.
 */
const REPORTED_COLOR = '#0f766e';
const GRID = [0, 25, 50, 75, 100];

export function ProgressCurveChart({
  curve,
  height,
  locked = false,
}: {
  curve: PmisProgressCurve;
  /**
   * 최종 저장된 공정표인가 (사용자 지시 2026-09-10).
   *
   * <p>굳었으면 <b>고치라는 말을 하지 않는다</b> — 「일정을 넣으세요」 는 할 수 없는 일을 시키는 말이다.
   * 값이 왜 그런지는 그대로 말하되, 사람에게 시키는 문장만 뺀다.
   */
  locked?: boolean;
  /**
   * 카드 높이를 못 박는다(선택). 그래프는 가로 폭에 따라 키가 정해지는데, 옆에 표를 세운 자리에서는
   * 그 키가 표와 달라 <b>아래 모서리가 어긋난다</b>(2026-08-26 지시). 값을 주면 카드가 그 높이를
   * 지키고 그림이 남는 공간을 채운다 — 비율은 그대로다(늘려 그리면 곡선이 거짓말을 한다).
   */
  height?: string;
}) {
  /*
    ★ 그릴 자리를 <b>잰다</b> (사용자 지시 2026-09-15 「이 표 반응형으로 하라니까?」).

    viewBox 를 720×240 으로 못 박아 두었더니, 곡선 패널을 가로 전체로 내보낸 뒤에도 그래프는
    그 비율 그대로 <b>가운데 박혀 있고 좌우가 하얗게</b> 남았다 — SVG 기본이 «비율을 지켜 맞춘다»
    (`preserveAspectRatio="xMidYMid meet"`)라서다. 늘려 그리면(`none`) 글자와 선이 찌그러진다.

    <p>그래서 viewBox 를 <b>상자와 같은 크기</b>로 준다: 한 단위가 곧 한 화면 픽셀이 되어
    늘어나지도 찌그러지지도 않고, 글자는 어느 폭에서나 같은 크기로 읽힌다.
  */
  const box = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = box.current;
    if (!node) return undefined;
    // 창 크기뿐 아니라 <b>옆 칸이 바뀔 때</b>도 다시 재야 한다(탭 전환·사이드바 접기).
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      setSize({ width: Math.round(rect.width), height: Math.round(rect.height) });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const plot = plotOf(size.width, size.height);

  if (curve.points.length === 0) {
    return (
      <Notice tone="info">
        {locked
          ? '계획 일정(시작일·종료일)이 있는 공정이 없어 그래프를 그리지 못합니다.'
          : '공정율 그래프를 그리려면 계획 일정(시작일·종료일)이 있는 공정이 필요합니다. [공정표 만들기] 에서 넣으면 그때부터 그려집니다.'}
      </Notice>
    );
  }

  const dates = curve.points.map((point) => point.date);
  const now = today();
  const marker = todayIndex(dates, now);
  const gap = currentGap(curve, now);
  const ticks = monthTicks(dates);

  const plannedPath = linePath(curve.points.map((point) => point.plannedRate), plot);
  const reportedPath = linePath(curve.points.map((point) => point.reportedRate), plot);

  return (
    <div className="card p-3 space-y-2" style={{ height, display: 'flex', flexDirection: 'column' }}>
      <div className="flex items-center gap-3 flex-wrap" style={{ fontSize: '12px' }}>
        <b style={{ fontSize: '13px' }}>공정율 그래프</b>
        <Legend color={PLANNED_COLOR} label={curve.fromFile ? '예정 (파일)' : '예정 (추정)'} dashed />
        <Legend color={REPORTED_COLOR} label="실적 (주간회의)" />
        {gap && (
          // 곡선을 보는 이유는 모양이 아니라 "지금 앞서는가 밀리는가" 다. 그 답을 글로도 적는다.
          <span style={{ marginLeft: 'auto', fontWeight: 700, color: gap.gap >= 0 ? '#15803d' : '#b91c1c' }}>
            {gap.gap >= 0 ? '계획 대비 선행' : '계획 대비 지연'} {Math.abs(gap.gap).toFixed(1)}%p
            <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>
              {' '}
              (예정 {gap.planned.toFixed(1)} / 실적 {gap.actual.toFixed(1)})
            </span>
          </span>
        )}
      </div>

      {/*
        상자가 크기를 정하고, 그림이 그 상자를 <b>꽉 채운다</b>. viewBox 가 잰 크기와 같아서
        한 단위가 곧 한 화면 픽셀이다 — 늘어나지도 찌그러지지도 않고 글자는 늘 같은 크기다.
        높이를 안 받으면(대화 카드) 예전만큼의 자리를 스스로 잡는다.
      */}
      <div
        ref={box}
        style={height ? { flex: 1, minHeight: 0 } : { height: '260px' }}
      >
        <svg
          viewBox={`0 0 ${plot.width} ${plot.height}`}
          style={{ width: '100%', height: '100%', display: 'block' }}
          role="img"
          aria-label="예정 공정율과 실적 공정율 곡선"
        >
          {GRID.map((rate) => (
            <g key={rate}>
              <line
                x1={CHART.padLeft}
                y1={yAt(rate, plot)}
                x2={CHART.padLeft + plot.plotWidth}
                y2={yAt(rate, plot)}
                stroke="#e2e8f0"
                strokeWidth={1}
              />
              <text x={CHART.padLeft - 6} y={yAt(rate, plot) + 3} textAnchor="end" fontSize={10} fill="#94a3b8">
                {rate}%
              </text>
            </g>
          ))}

          {ticks.map((tick) => (
            <text
              key={tick.index}
              x={xAt(tick.index, dates.length, plot)}
              y={plot.height - 8}
              textAnchor="middle"
              fontSize={10}
              fill="#94a3b8"
            >
              {tick.label}
            </text>
          ))}

          {/* 오늘 기준선. 두 곡선의 간격을 어디서 읽어야 하는지 가리킨다. */}
          {marker != null && (
            <>
              <line
                x1={xAt(marker, dates.length, plot)}
                y1={CHART.padTop}
                x2={xAt(marker, dates.length, plot)}
                y2={plot.height - CHART.padBottom}
                stroke="#f59e0b"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
              <text
                x={xAt(marker, dates.length, plot)}
                y={CHART.padTop - 2}
                textAnchor="middle"
                fontSize={10}
                fill="#f59e0b"
              >
                오늘
              </text>
            </>
          )}

          <path d={plannedPath} fill="none" stroke={PLANNED_COLOR} strokeWidth={2} strokeDasharray="5 4" />
          <path d={reportedPath} fill="none" stroke={REPORTED_COLOR} strokeWidth={2.5} />
        </svg>
      </div>

      <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
        {curve.start} ~ {curve.end} ·{' '}
        {curve.fromFile ? (
          // 파일에서 읽은 곡선이면 그렇게 말한다 — 이 값은 우리가 정한 것이 아니다.
          <b>예정은 공정표 파일의 계획공정율 그대로</b>
        ) : (
          <>
            예정은 착수·완료일로 <b>추정한 S-curve</b>입니다
          </>
        )}
        , 실적은 주간회의에 보고한 공정율입니다.
        {!locked && !curve.fromFile && curve.datedCount < curve.leafCount && (
          // 숨기지 않는다 — 예정 곡선이 100% 에 못 닿는 이유를 화면이 말해야 한다.
          // 다만 굳은 뒤에는 말하지 않는다: 고칠 수 없는 것을 두고 「없다」 고만 하면 탓하는 말이 된다.
          <>
            {' '}
            <b style={{ color: '#b45309' }}>
              공정 {curve.leafCount}개 중 {curve.leafCount - curve.datedCount}개에 계획 일정이 없어 예정 곡선이 100% 에
              닿지 않습니다.
            </b>
          </>
        )}
      </p>
    </div>
  );
}

function Legend({ color, label, dashed = false }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span className="flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
      <svg width={18} height={8} aria-hidden>
        <line
          x1={0}
          y1={4}
          x2={18}
          y2={4}
          stroke={color}
          strokeWidth={2.5}
          strokeDasharray={dashed ? '5 4' : undefined}
        />
      </svg>
      {label}
    </span>
  );
}
